import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  ShoppingBag,
  Home,
  Calendar,
  CalendarHeart,
  MessageSquare,
  Camera,
  Users,
  FileText,
  MessageCircle,
  Shield,
  Landmark,
  Sparkles,
  User as UserIcon,
  LogOut,
  Plus,
  ArrowRight,
  Command,
  type LucideIcon,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'

interface CommandItem {
  id: string
  label: string
  hint?: string
  icon: LucideIcon
  keywords?: string[]
  group: 'Navigate' | 'Actions' | 'Account'
  action: () => void
  /** Restrict visibility to ADMIN/BOARD. */
  requiresAdmin?: boolean
}

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

/**
 * Cmd+K command palette. Opens from any page via the header search button or
 * the keyboard shortcut. Filters across navigation destinations and quick
 * actions. Uses arrow keys + Enter, Escape to close.
 *
 * Premium calibration: floats with shadow-float, subtle scale-in, results
 * highlighted by group with eyebrow labels, hover and active states share
 * the same selection color so keyboard nav feels identical to mouse.
 */
const CommandPalette = ({ open, onClose }: CommandPaletteProps) => {
  const navigate = useNavigate()
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'ADMIN' || role === 'BOARD'
  const signOut = useAuthStore((s) => s.signOut)

  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const goAndClose = (path: string) => {
    navigate(path)
    onClose()
  }

  const items: CommandItem[] = useMemo(
    () => [
      // Navigate
      { id: 'nav-home', label: 'Dashboard', icon: Home, group: 'Navigate', action: () => goAndClose('/dashboard'), keywords: ['home', 'overview'] },
      { id: 'nav-shop', label: 'Shop', icon: ShoppingBag, group: 'Navigate', action: () => goAndClose('/shop'), keywords: ['merchandise', 'shirts', 'hats', 'sweatshirts', 'clothing'] },
      { id: 'nav-cal', label: 'Calendar', icon: Calendar, group: 'Navigate', action: () => goAndClose('/calendar') },
      { id: 'nav-events', label: 'Events', icon: CalendarHeart, group: 'Navigate', action: () => goAndClose('/events'), keywords: ['rsvp', 'gathering'] },
      { id: 'nav-bull', label: 'Bulletin', icon: MessageSquare, group: 'Navigate', action: () => goAndClose('/bulletin'), keywords: ['posts', 'board', 'community'] },
      { id: 'nav-album', label: 'Album', icon: Camera, group: 'Navigate', action: () => goAndClose('/album'), keywords: ['photos', 'pictures', 'memories'] },
      { id: 'nav-dir', label: 'Directory', icon: Users, group: 'Navigate', action: () => goAndClose('/directory'), keywords: ['people', 'cottages', 'members'] },
      { id: 'nav-board', label: 'Board', icon: Landmark, group: 'Navigate', action: () => goAndClose('/board'), keywords: ['directors', 'governance', 'officers'] },
      { id: 'nav-docs', label: 'Documents', icon: FileText, group: 'Navigate', action: () => goAndClose('/documents'), keywords: ['files', 'bylaws', 'minutes'] },
      { id: 'nav-messages', label: 'Messages', icon: MessageCircle, group: 'Navigate', action: () => goAndClose('/messages'), keywords: ['dm', 'direct', 'chat', 'inbox'] },
      { id: 'nav-help', label: 'Help Desk', hint: 'Ask the AI assistant anything', icon: Sparkles, group: 'Navigate', action: () => goAndClose('/help'), keywords: ['ai', 'assistant', 'chat', 'support', 'help', 'question', 'weather'] },
      ...(isAdmin
        ? [{ id: 'nav-admin', label: 'Admin', hint: 'Treasurer tools', icon: Shield, group: 'Navigate' as const, action: () => goAndClose('/admin'), keywords: ['board', 'treasurer'] }]
        : []),

      // Actions
      { id: 'act-post', label: 'New bulletin post', hint: 'Share something with the lake', icon: Plus, group: 'Actions', action: () => goAndClose('/bulletin?compose=1'), keywords: ['create', 'write'] },
      { id: 'act-event', label: 'Add event', hint: 'Schedule a gathering', icon: Plus, group: 'Actions', action: () => goAndClose('/events?new=1') },
      { id: 'act-photo', label: 'Upload photos', hint: 'Add to the album', icon: Plus, group: 'Actions', action: () => goAndClose('/album?upload=1') },

      // Account
      { id: 'acc-profile', label: 'Profile & settings', icon: UserIcon, group: 'Account', action: () => goAndClose('/profile') },
      { id: 'acc-signout', label: 'Sign out', icon: LogOut, group: 'Account', action: async () => { onClose(); await signOut() } },
    ],
    [isAdmin] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter((it) => {
      const haystack = [it.label, it.hint ?? '', ...(it.keywords ?? [])].join(' ').toLowerCase()
      return haystack.includes(q)
    })
  }, [items, query])

  // Group filtered items by group, preserving original order.
  const grouped = useMemo(() => {
    const map = new Map<CommandItem['group'], CommandItem[]>()
    for (const it of filtered) {
      const list = map.get(it.group) ?? []
      list.push(it)
      map.set(it.group, list)
    }
    return Array.from(map.entries())
  }, [filtered])

  // Reset selection when query changes
  useEffect(() => setActiveIndex(0), [query])

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIndex(0)
      // focus input on next tick so the modal mounts first
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  // Keyboard navigation
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(filtered.length - 1, i + 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(0, i - 1))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const item = filtered[activeIndex]
        if (item) item.action()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, filtered, activeIndex])

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-cmd-index="${activeIndex}"]`) as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  if (!open) return null

  let runningIndex = 0

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[12vh] px-4 bg-deepwater/40 backdrop-blur-md animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cmd-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-paper rounded-3xl shadow-float overflow-hidden border border-mist/40"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-mist/40">
          <Search className="w-4 h-4 text-slate" aria-hidden="true" />
          <input
            ref={inputRef}
            id="cmd-title"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the portal — pages, actions, members…"
            className="flex-1 bg-transparent text-deepwater placeholder:text-slate/60 focus:outline-none text-sm"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-eyebrow font-semibold text-slate bg-mist/40 px-2 py-1 rounded-md">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[55vh] overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-slate text-sm italic">
              Nothing matches "{query}"
            </div>
          ) : (
            grouped.map(([group, list]) => (
              <div key={group} className="px-2 py-1">
                <div className="px-3 pt-2 pb-1 text-[0.6rem] uppercase tracking-eyebrow font-semibold text-slate/70">
                  {group}
                </div>
                {list.map((item) => {
                  const myIndex = runningIndex++
                  const isActive = myIndex === activeIndex
                  const Icon = item.icon
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-cmd-index={myIndex}
                      onMouseEnter={() => setActiveIndex(myIndex)}
                      onClick={item.action}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-deepwater hover:bg-mist/30'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          isActive ? 'bg-primary/15' : 'bg-mist/40'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{item.label}</div>
                        {item.hint && (
                          <div className="text-xs text-slate truncate">{item.hint}</div>
                        )}
                      </div>
                      {isActive && <ArrowRight className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-3 border-t border-mist/40 bg-paper-fade flex items-center justify-between text-[0.7rem] text-slate">
          <span className="inline-flex items-center gap-2">
            <Command className="w-3 h-3" />
            Quick navigate
          </span>
          <span className="inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <kbd className="bg-mist/40 px-1.5 py-0.5 rounded">↑</kbd>
              <kbd className="bg-mist/40 px-1.5 py-0.5 rounded">↓</kbd>
              navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="bg-mist/40 px-1.5 py-0.5 rounded">↵</kbd>
              select
            </span>
          </span>
        </div>
      </div>
    </div>
  )
}

export default CommandPalette
